import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { getGeneralApiProblem } from "../../services/api/api-problem"
import {
  Measurement,
} from "../../database"
import config from "../../utils/config"
import codePushCheck from "../../utils/code-push"
import inAppUpdateCheck from "../../utils/in-app-update"
import { Toast } from "native-base"
import { Intercom } from "../../components"
import { Platform } from "react-native"
import { createAxios } from "../../utils/axios"

const HomeStoreProps = {
  loading: types.maybeNull(types.boolean),
  refreshing: types.maybeNull(types.boolean),
  navigating: types.maybeNull(types.boolean),

  error: types.maybeNull(types.frozen()),
  sponsors: types.maybeNull(types.frozen()),
  additionalMenus: types.maybeNull(types.frozen()),
  menuLoading: types.maybeNull(types.boolean),

  unreadNotificationsCount: types.maybeNull(types.number),
  unreadConversationCount: types.maybeNull(types.number),

  lastUpdateDay: types.optional(types.string, '')
}

/**
 * Model description here for TypeScript hints.
 */
export const HomeStoreModel = types
  .model("HomeStore")
  .props(HomeStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .views((self) => ({})) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof HomeStoreProps, value: any) {
      if (self[key] === value) return;
      self[key] = value as never;
    },
    async fetchSponsors() {
      const params = { code__in: 'DA01,DA02,DA03,DA04,DA05,DA06,DA07,DA08,DA09,DA10', sort: 'code' };
      const result: any = await self.apiSauce.get("/sponsors", params)
      if (result.status === 200) {
        const { data } = result.data;
        const { BASE_URL } = config()
        const sponsors = data.filter((r) => r.media).map((r) => ({
          image: {
            uri: (r.media || '').startsWith('http') ? r.media : `${BASE_URL}/api${r.media}`,
            // eslint-disable-next-line dot-notation
            get headers() {
              return this.uri.startsWith(BASE_URL) ? { Authorization: self.environment.api.apisauce.headers.Authorization } : undefined
            }
          },
          link: r.target_url,
          mixpanelKey: `Mobile: promo card - ${data.code}`,
        }));
        this.set('error', null)
        this.set('sponsors', sponsors)
      } else {
        __DEV__ && console.warn(result.status)
        // this.set('sponsors', [])
        this.set('error', getGeneralApiProblem(result))
      }
    },
    async getUnreadNotificationsCount() {
      return self.axios.get('/notifications', { params: { per_page: 1, read_at__null: 1 } })
        .then((response) => {
          const { data: { meta } } = response;
          this.set('unreadNotificationsCount', meta.total);
          return response;
        })
        .catch((e) => {
          this.set('unreadNotificationsCount', null);
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        })
    },
    async getUnreadConversationCount() {
      if (Platform.OS === 'web') {
        this.set('unreadConversationCount', null);
        return null;
      }
      return createAxios().get('/ping')
        .then(() => Intercom.getUnreadConversationCount())
        .catch((e) => {
          if (!Toast.isActive(e.name))
            Toast.show({ id: e.name, title: e.message })
          return 0;
        })
        .then((count) => {
          this.set('unreadConversationCount', count);
          return count;
        })
    },
    async init () {
      const { tokenStore: { token } } = self.rootStore

      if (!token) return;
      self.environment.api.apisauce.setHeader('Authorization', `Bearer ${token.access_token}`)
      // Need to be triggered manually
      new Measurement()._bootIfNotBooted();
      this.set('loading', false);
      this.set('navigating', false)
      this.set('refreshing', false);
      this.fetchSponsors().catch();
      self.rootStore.occupationStore.init();
    },
    async initialRefresh() {
      const { appStore } = self.rootStore      
      return new Promise<void>((resolve, reject) => {
        appStore.getFarms(false).then(() => {
          if (appStore.farmStore)
            appStore.farmStore.initialInitWithLastBatchAndWaterQualityMultiMeasurements(() => {
              appStore.syncFarmsWithOthers()
              resolve();
            });
          else {
            appStore.syncFarmsWithOthers()
            resolve();
          }
        }).catch(reject);
      })
    },
    async refresh() {
      const { appStore, userStore } = self.rootStore

      return new Promise<void>((resolve, reject) => {
        appStore.getFarms(true, (success) => {
          if (success && appStore.farmStore) {
            appStore.farmStore.initWithLastBatchAndWaterQualityMultiMeasurements(() => {
              userStore.updateLocale().then(() => userStore.getUser()).finally(resolve);
            });
          }
          else {
            userStore.updateLocale().then(() => userStore.getUser()).finally(resolve);
          }
        }).catch(reject);
      })
    },
    async refreshBatch() {
      const { appStore } = self.rootStore

      return new Promise<void>((resolve) => {
        if (appStore.farmStore)
          appStore.farmStore.initWithLastBatch(resolve);
        else resolve();
      })
    },
    async onRefresh () {
      // if (self.refreshing) return;
      await self.rootStore.appStore.checkNetworkConnection().catch()
      await this.init();
      this.set('refreshing', true);
      this.refresh().finally(async () => {
        this.set('refreshing', false);
        codePushCheck().catch();
        inAppUpdateCheck().catch();
        this.getUnreadNotificationsCount().catch();
        this.getUnreadConversationCount().catch();
        self.rootStore.shrimpPriceStore.set('filterShrimpPrice', {});
        await self.rootStore.shrimpPriceStore.getShrimpPrices();
        await self.rootStore.newsStore.fetchNews();
        await self.rootStore.diseaseStore.getDiseases();
        await self.rootStore.widgetStore.getWidgets();
      })
    },
    async onRefreshBatch () {
      // if (self.refreshing) return;

      await this.init();
      this.set('refreshing', true);
      this.refreshBatch().finally(() => {
        this.set('refreshing', false);
      })
    },
    async fetchAdditionalMenus () {
      this.set('menuLoading', true)
      const result: any = await self.axios.get("/app_menus")
      if (result.status === 200) {
        const { data } = result.data;
        const { BASE_URL } = config()
        const menus = data.map((r) => ({
          ...r,
          icon: r.icon ? {
            uri: (r.icon || '').startsWith('http') ? r.icon : `${BASE_URL}/api${r.icon}`,
            // eslint-disable-next-line dot-notation
            get headers() {
              return this.uri.startsWith(BASE_URL) ? { Authorization: self.environment.api.apisauce.headers.Authorization } : undefined
            }
          } : null,
          image: r.image ? {
            uri: (r.image || '').startsWith('http') ? r.image : `${BASE_URL}/api${r.image}`,
            // eslint-disable-next-line dot-notation
            get headers() {
              return this.uri.startsWith(BASE_URL) ? { Authorization: self.environment.api.apisauce.headers.Authorization } : undefined
            }
          } : null,
          // link: r.target_url,
          mixpanelKey: `Mobile: additional menu`,
        }));
        this.set('error', null)
        this.set('menuLoading', false)
        this.set('additionalMenus', menus)
      } else {
        __DEV__ && console.warn(result.status)
        // this.set('additionalMenus', [])
        this.set('menuLoading', false)
        this.set('error', getGeneralApiProblem(result))
      }
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars


type HomeStoreType = Instance<typeof HomeStoreModel>
export interface HomeStore extends HomeStoreType {}
type HomeStoreSnapshotType = SnapshotOut<typeof HomeStoreModel>
export interface HomeStoreSnapshot extends HomeStoreSnapshotType {}
export const createHomeStoreDefaultModel = () => types.optional(HomeStoreModel, {})
